/**
 * HeatmapTable — hourly heatmap row builders.
 * rows: Array of { hour: 'HH:MM', q1, q2, q3, q4, total }
 * Cell colors come from `heatScale.js` (`heatCell`), which bins each value onto
 * a Meridian base-color ramp; the Total column is scaled independently from
 * quarters by the renderer.
 */

export function buildEmptyHeatmapRows(startHour, numHours = 24) {
    return Array.from({ length: numHours }, (_, slotIndex) => {
        const hourOfDay = (startHour + slotIndex) % 24;
        const pad = num => String(num).padStart(2, '0');
        return { hour: `${pad(hourOfDay)}:00`, q1: 0, q2: 0, q3: 0, q4: 0, total: 0 };
    });
}

export function timestampsToHeatmapRows(timestamps, startHour, startDateMs, numHours = 24, actualAvailableThrough = null) {
    const rows = buildEmptyHeatmapRows(startHour, numHours);
    if (actualAvailableThrough != null) {
        rows.forEach((row, index) => {
            row.actualAvailable = startDateMs + index * 3600000 < actualAvailableThrough;
        });
    }
    const windowMs = numHours * 3600000;
    for (const timestamp of timestamps) {
        const offsetMs   = timestamp * 1000 - startDateMs;
        if (offsetMs < 0 || offsetMs >= windowMs) continue;
        const totalMins  = Math.floor(offsetMs / 60000);
        const hourIndex  = Math.floor(totalMins / 60);
        const quarter    = Math.floor((totalMins % 60) / 15);
        if (hourIndex < 0 || hourIndex >= numHours) continue;
        const row = rows[hourIndex];
        row.total++;
        if (quarter === 0) row.q1++;
        else if (quarter === 1) row.q2++;
        else if (quarter === 2) row.q3++;
        else row.q4++;
    }
    return rows;
}

export function completeEventsToDriverHeatmapRows(completeEvents, startHour, startDateMs, numHours = 24) {
    const rows = buildEmptyHeatmapRows(startHour, numHours);
    const driverSets = rows.map(() => [new Set(), new Set(), new Set(), new Set()]);
    const windowMs = numHours * 3600000;

    for (const event of completeEvents) {
        if (!event.userId) continue;
        const offsetMs  = event.timestamp * 1000 - startDateMs;
        if (offsetMs < 0 || offsetMs >= windowMs) continue;
        const totalMins = Math.floor(offsetMs / 60000);
        const hourIndex = Math.floor(totalMins / 60);
        const quarter   = Math.floor((totalMins % 60) / 15);
        if (hourIndex < 0 || hourIndex >= numHours) continue;
        driverSets[hourIndex][quarter].add(event.userId);
    }

    rows.forEach((row, hourIndex) => {
        row.q1 = driverSets[hourIndex][0].size;
        row.q2 = driverSets[hourIndex][1].size;
        row.q3 = driverSets[hourIndex][2].size;
        row.q4 = driverSets[hourIndex][3].size;
        const uniqueDriversThisHour = new Set([
            ...driverSets[hourIndex][0], ...driverSets[hourIndex][1],
            ...driverSets[hourIndex][2], ...driverSets[hourIndex][3]
        ]);
        row.total = uniqueDriversThisHour.size;
    });

    return rows;
}
